import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { HpCardHeader, HpTag, PageHeading } from '@hp/ui';
import { useCan } from '../api/me';
import { useMergeJob } from '../features/jobs/queries';
import { EntityBadge, ModeBadge, StatusBadge } from '../components/badges';
import { ErrorAlert } from '../components/error-alert';
import { RelativeTime } from '../components/relative-time';
import { Spinner } from '../components/ui/icons';
import { JobControls } from '../features/jobs/job-controls';
import { Progress } from '../features/jobs/job-progress';
import { TaskTable } from '../features/jobs/task-table';
import { prettyJson, statsSummary } from '../lib/format';

function BackLink() {
  const { t } = useTranslation(['jobs', 'common']);
  return (
    <Link to="/crm-data/jobs" className="hp-detail-action hp-detail-action--text">
      {t('backToJobs')}
    </Link>
  );
}

export default function JobDetail() {
  const { t } = useTranslation(['jobs', 'common']);
  const params = useParams();
  const id = Number(params.id);
  const can = useCan();
  const query = useMergeJob(id);

  if (query.isLoading) return <Spinner>{t('loadingJob')}</Spinner>;
  if (query.error) {
    return (
      <div className="bo-sections">
        <BackLink />
        <ErrorAlert error={query.error} />
      </div>
    );
  }
  if (!query.data) return null;
  const { job, tasks } = query.data;

  return (
    <div className="bo-sections">
      <PageHeading
        title={t('detail.title')}
        recordId={`#${job.id}`}
        meta={
          <div className="bo-row bo-job-meta">
            <ModeBadge mode={job.mode} />
            <EntityBadge entityType={job.entityType} />
            <StatusBadge status={job.status} />
            {job.cap !== null && <HpTag tone="neutral">{t('detail.cap', { cap: job.cap })}</HpTag>}
            {job.backupRunId !== null && <HpTag tone="neutral">{t('detail.backupRun', { id: job.backupRunId })}</HpTag>}
          </div>
        }
        actions={<BackLink />}
      />

      <section className="hp-card">
        <HpCardHeader title={t('detail.progress')} icon="merge">
          {can('crm.merge') && <JobControls job={job} />}
        </HpCardHeader>
        <div className="hp-card__body bo-sections">
          <Progress tasks={tasks} />
          <div className="bo-row bo-muted bo-job-times">
            <span>
              {t('detail.created')} <RelativeTime value={job.createdAt} />
            </span>
            <span>
              {t('detail.started')} <RelativeTime value={job.startedAt} />
            </span>
            <span>
              {t('detail.finished')} <RelativeTime value={job.finishedAt} />
            </span>
            {job.stats && (
              <span className="bo-mono bo-job-stats" title={prettyJson(job.stats)}>
                {statsSummary(job.stats, 120)}
              </span>
            )}
          </div>
        </div>
      </section>

      <TaskTable tasks={tasks} />
    </div>
  );
}
